<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── HEADER ── -->
      <div class="wm-row">
        <div class="wm-line"></div>
        <span class="wm-text">Club Trailz</span>
        <div class="wm-line r"></div>
      </div>

      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Legal</span>
        <div class="section-rule"></div>
      </div>
      <h1 class="page-title">NDA &amp; Code <em>of Conduct</em></h1>
      <p class="page-sub">Effective as of your acceptance during registration · Last updated {{ lastUpdated }}</p>

      <!-- ── DOCUMENT ── -->
      <div class="doc">

        <section class="doc-section">
          <h2 class="doc-h2">1. Purpose</h2>
          <p class="doc-p">
            Club Trailz exists to connect members of reciprocal private clubs, share recommendations across
            cities, and build the case for official arrangements with the businesses members already frequent.
            Membership depends on trust between members and in the discretion of the network itself. This agreement
            sets the terms of that trust.
          </p>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">2. Confidentiality</h2>
          <p class="doc-p">By accepting this agreement, you agree that you will not:</p>
          <ul class="doc-list">
            <li>Share another member's contact information, profile details, club affiliation, or travel plans outside the platform without their consent.</li>
            <li>Screenshot, export, scrape, or otherwise reproduce member directories, recommendations, or conversations for use outside the network.</li>
            <li>Use information gained through the network to contact members for commercial solicitation, recruiting, or unrelated business development.</li>
            <li>Disclose non-public information about the platform's operations, membership numbers, or business arrangements shared by Club Trailz team.</li>
          </ul>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">3. Code of Conduct</h2>
          <p class="doc-p">As a member, you agree to:</p>
          <ul class="doc-list">
            <li>Represent your club affiliation accurately. Membership is granted on the basis of verified reciprocal club affiliation, and misrepresenting it is grounds for removal.</li>
            <li>Engage other members with the same courtesy and discretion expected within your home club.</li>
            <li>Post recommendations, travel plans, and chapter discussion in good faith — recommendations should reflect genuine experience, not paid promotion or personal commercial interest, unless clearly disclosed.</li>
            <li>Refrain from harassment, discriminatory conduct, or any behavior that would not be tolerated within a private club setting.</li>
            <li>Use direct messaging and introductions for their intended purpose — building genuine connections, not solicitation or spam.</li>
          </ul>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">4. Business Use of Recommendations</h2>
          <p class="doc-p">
            When you request that Club Trailz pursue an official arrangement with a business, you authorize us
            to share aggregated, anonymized demand data (such as the number of members requesting an arrangement)
            with that business as part of pursuing the arrangement. We will not share your individual identity or
            personal information with a business without your separate, explicit consent.
          </p>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">5. Consequences of Breach</h2>
          <p class="doc-p">
            Violation of this agreement may result in a warning, suspension, or permanent removal from the network at
            Club Trailz's discretion, without refund of any fees paid. Serious breaches of confidentiality may
            also expose you to legal liability independent of any action taken by Club Trailz.
          </p>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">6. Term</h2>
          <p class="doc-p">
            This agreement remains in effect for as long as you hold an account with Club Trailz, and your
            confidentiality obligations under Section 2 survive termination of your membership.
          </p>
        </section>

        <section class="doc-section">
          <h2 class="doc-h2">7. Acknowledgment</h2>
          <p class="doc-p">
            By checking "I agree to the NDA &amp; Code of Conduct" during registration, you acknowledge that you have
            read, understood, and agree to be bound by the terms above.
          </p>
        </section>

      </div>

      <button class="btn-close" @click="handleClose">
        <div class="btn-tick"></div>
        <span>Close</span>
        <div class="btn-tick"></div>
      </button>

    </div>
  </div>
</template>

<script>
export default {
  name: "NdaConductView",

  computed: {
    lastUpdated() {
      return new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    },
  },

  methods: {
    handleClose() {
      // This route is meant to be opened in a new tab from the registration
      // form (so filling out the multi-step form isn't lost), so closing the
      // tab is the natural action. Fall back to router history if it's not
      // actually a standalone tab (e.g. opened via in-app navigation).
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        window.close();
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 720px; margin: 0 auto; padding: 56px 24px 100px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── WORDMARK ── */
.wm-row { display: flex; align-items: center; gap: 14px; margin-bottom: 48px; }
.wm-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2)); }
.wm-line.r { background: linear-gradient(90deg, rgba(210,175,100,0.2), transparent); }
.wm-text { font-size: 11px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.5); text-transform: uppercase; white-space: nowrap; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); flex: 0 0 24px; }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.page-title { font-family: 'Cormorant', serif; font-size: 44px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 10px; line-height: 1.1; }
.page-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.page-sub { font-size: 12px; font-weight: 500; letter-spacing: 1px; color: rgba(237,232,223,0.25); text-transform: uppercase; margin-bottom: 28px; }

/* ── DISCLAIMER ── */

/* ── DOCUMENT ── */
.doc { display: flex; flex-direction: column; gap: 36px; margin-bottom: 48px; }
.doc-section { border-top: 1px solid rgba(210,175,100,0.08); padding-top: 28px; }
.doc-section:first-child { border-top: none; padding-top: 0; }
.doc-h2 { font-family: 'Cormorant', serif; font-size: 24px; font-weight: 400; color: #edeae2; margin-bottom: 14px; letter-spacing: -0.2px; }
.doc-p { font-size: 14px; font-weight: 300; line-height: 1.85; color: rgba(237,232,223,0.55); margin-bottom: 12px; }
.doc-p:last-child { margin-bottom: 0; }
.doc-list { display: flex; flex-direction: column; gap: 12px; padding-left: 20px; }
.doc-list li { font-size: 14px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.5); list-style: none; position: relative; }
.doc-list li::before { content: ''; position: absolute; left: -20px; top: 9px; width: 5px; height: 5px; border-radius: 50%; background: rgba(210,175,100,0.5); }

/* ── CLOSE BUTTON ── */
.btn-close { width: 100%; display: flex; align-items: center; justify-content: center; gap: 14px; border: 1px solid rgba(210,175,100,0.3); background: transparent; padding: 16px; color: rgba(210,175,100,0.8); font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; cursor: pointer; transition: border-color 0.3s, background 0.3s; }
.btn-close:hover { border-color: rgba(210,175,100,0.6); background: rgba(210,175,100,0.06); }
.btn-tick { width: 18px; height: 1px; background: rgba(210,175,100,0.4); flex-shrink: 0; }

@media (max-width: 600px) { .page { padding: 40px 16px 80px; } .frame, .frame-inner { inset: 12px; } .page-title { font-size: 32px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>